'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

export type MatrixDeliveryRequest = {
  id: string;
  sku: string;
  quantity: number;
  options: Record<string, unknown>;
  deliveryPromise?: number;
};

export type MatrixDeliveryResult = {
  id: string;
  status: 'confirmed' | 'pending' | 'unavailable';
  supplierDeliveryDate?: string;
  supplierLatestDeliveryDate?: string;
  deliveryDate?: string;
  latestDeliveryDate?: string;
  expiresAt?: string;
  retryAfterSeconds?: number;
};

/** Query only the quantity whose delivery dates are shown in the headers. */
export function selectedQuantityDeliveryRequests(
  cells: Record<string, { available: boolean; deliveryRequest?: MatrixDeliveryRequest }>,
  quantity: number,
) {
  return Object.values(cells)
    .filter((cell) => cell.available && cell.deliveryRequest)
    .map((cell) => cell.deliveryRequest!)
    .sort((a, b) => Number(b.id === `${quantity}:standard`) - Number(a.id === `${quantity}:standard`) || a.id.localeCompare(b.id));
}

export function visibleDeliverySpeedIds(ids: string[], quantity: number, results: Map<string, MatrixDeliveryResult>) {
  const standard = results.get(`${quantity}:standard`);
  return ids.filter(id => {
    if (id === 'standard') return true;
    const result = results.get(`${quantity}:${id}`);
    if (result?.status !== 'confirmed' || !result.deliveryDate) return false;
    return standard?.status !== 'confirmed' || result.deliveryDate !== standard.deliveryDate
      || (result.latestDeliveryDate || result.deliveryDate) !== (standard.latestDeliveryDate || standard.deliveryDate);
  });
}

/** Dates are fetched separately so an unavailable date never hides a valid price. */
export function usePrintComDeliveryAvailability(
  items: MatrixDeliveryRequest[],
  countryCode: string,
  enabled: boolean,
) {
  const requestKey = JSON.stringify({ countryCode, items: enabled ? items : [] });
  const [snapshot, setSnapshot] = useState<{ key: string; results: MatrixDeliveryResult[] }>({ key: '', results: [] });
  const completedSelections = useRef(new Map<string, MatrixDeliveryResult[]>());
  const lastSelectionKey = useRef('');

  useEffect(() => {
    const request = JSON.parse(requestKey) as { countryCode: string; items: MatrixDeliveryRequest[] };
    if (!request.items.length) return;
    const sameSelection = lastSelectionKey.current === requestKey;
    lastSelectionKey.current = requestKey;
    const completed = completedSelections.current.get(requestKey);
    if (completed && (sameSelection || completed.every(result => result.expiresAt && Date.parse(result.expiresAt) > Date.now()))) {
      setSnapshot({ key: requestKey, results: completed }); return;
    }
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let results: MatrixDeliveryResult[] = request.items.map((item) => ({ id: item.id, status: 'pending' }));
    const unavailable = () => setSnapshot({ key: requestKey, results: results.map((result) => result.status === 'pending' ? { ...result, status: 'unavailable' } : result) });
    const run = async () => {
      if (controller.signal.aborted) return;
      if (attempts >= Math.min(80, request.items.length + 12)) { unavailable(); return; }
      if (document.visibilityState === 'hidden') { timer = setTimeout(run, 10_000); return; }
      attempts += 1;
      try {
        const pendingIds = new Set(results.filter((result) => result.status === 'pending').map((result) => result.id));
        // The server shares cache and leases across instances and permits at
        // most one new supplier lookup; this is not one request per matrix cell.
        const response = await fetch('/api/printcom/delivery-availability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ countryCode: request.countryCode, items: request.items.filter((item) => pendingIds.has(item.id)).slice(0, 20) }),
          signal: controller.signal,
        });
        const data = await response.json();
        if (controller.signal.aborted) return;
        if (!response.ok || !Array.isArray(data.results)) { unavailable(); return; }
        const next = new Map<string, MatrixDeliveryResult>(data.results.map((result: MatrixDeliveryResult) => [result.id, result]));
        results = results.map((result) => next.get(result.id) || result);
        setSnapshot({ key: requestKey, results });
        const pending = results.filter((result) => result.status === 'pending');
        // This selection is finished. Cache expiry controls future selections,
        // and must not trigger supplier requests while the page is left open.
        if (!pending.length) {
          completedSelections.current.set(requestKey, results);
          if (completedSelections.current.size > 20) completedSelections.current.delete(completedSelections.current.keys().next().value!);
          return;
        }
        const delay = Math.max(10, Math.min(...pending.map((result) => Number(result.retryAfterSeconds) || 10)));
        timer = setTimeout(run, Math.min(3600, delay) * 1000);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') return;
        // Never substitute a fabricated date after a failure.
        unavailable();
      }
    };
    timer = setTimeout(run, 300);
    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [requestKey]);

  return useMemo(() => new Map(
    (snapshot.key === requestKey ? snapshot.results : []).map((result) => [result.id, result]),
  ), [snapshot, requestKey]);
}

export function matrixDeliveryText(result: MatrixDeliveryResult | undefined, locale: string) {
  const language = locale.split('-')[0];
  if (result?.status === 'confirmed' && result.deliveryDate && /^\d{4}-\d{2}-\d{2}$/.test(result.deliveryDate)) {
    const date = new Date(`${result.deliveryDate}T12:00:00Z`);
    if (Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === result.deliveryDate) {
      const formatter = new Intl.DateTimeFormat(({ nl: 'nl-NL', en: 'en-GB', de: 'de-DE', fr: 'fr-FR' } as Record<string, string>)[language] || 'en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Amsterdam',
      });
      const planned = formatter.format(date);
      if (result.latestDeliveryDate && /^\d{4}-\d{2}-\d{2}$/.test(result.latestDeliveryDate) && result.latestDeliveryDate > result.deliveryDate) {
        const latest = new Date(`${result.latestDeliveryDate}T12:00:00Z`);
        if (Number.isFinite(latest.getTime()) && latest.toISOString().slice(0, 10) === result.latestDeliveryDate) {
          return `${planned} – ${formatter.format(latest)}`;
        }
      }
      return planned;
    }
  }
  if (result?.status === 'unavailable') {
    return ({ nl: 'Leverdatum niet beschikbaar', en: 'Delivery date unavailable', de: 'Liefertermin nicht verfügbar', fr: 'Date de livraison indisponible' } as Record<string, string>)[language] || 'Delivery date unavailable';
  }
  return ({ nl: 'Leverdatum wordt gecontroleerd', en: 'Checking delivery date', de: 'Liefertermin wird geprüft', fr: 'Vérification de la livraison' } as Record<string, string>)[language] || 'Checking delivery date';
}
